import React, { useEffect, useMemo, useState } from "react";
import { X, Gauge, Trophy, ChevronDown, LineChart as LineIcon } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, ReferenceDot,
} from "recharts";
import { fetchRisiko, fetchRisikoHistory } from "../lib/api";
import { titleCase, formatNumber } from "../lib/format";

const RANGE_MAP = { "7 Hari": "7d", "30 Hari": "30d", "All": "180d" };

const statusColor = (s) => {
  const t = (s || "").toLowerCase();
  if (t === "kritis") return "#ef4444";
  if (t === "waspada") return "#f59e0b";
  if (t === "siaga") return "#facc15";
  return "#10b981";
};

export const RisikoWilayahModal = ({ open, onClose }) => {
  const [provinces, setProvinces] = useState([]);
  const [selected, setSelected] = useState(null);
  const [range, setRange] = useState("7 Hari");
  const [history, setHistory] = useState([]);
  const [showMetrics, setShowMetrics] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchRisiko()
      .then((d) => {
        if (d?.success) {
          const list = d.list || [];
          setProvinces(list);
          if (list.length) setSelected(list[0]);
        }
      })
      .catch((err) => console.warn("Risiko fetch failed:", err));
  }, [open]);

  useEffect(() => {
    if (!selected || !open) return;
    fetchRisikoHistory(selected.code, RANGE_MAP[range] || "7d")
      .then((d) => {
        if (d?.history) {
          setHistory(
            d.history.map((p) => ({
              date: p.date ? new Date(p.date).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) : "",
              value: p.value,
            }))
          );
        }
      })
      .catch(() => setHistory([]));
  }, [selected, range, open]);

  const sortedProv = useMemo(() => {
    return [...provinces].sort((a, b) => {
      if (a.code === "100") return -1;
      if (b.code === "100") return 1;
      return (b.score || 0) - (a.score || 0);
    });
  }, [provinces]);

  if (!open || !selected) {
    return open ? (
      <div className="nms-modal-backdrop full-modal" data-testid="risiko-modal" onClick={onClose}>
        <div className="nms-modal risiko-modal" onClick={(e) => e.stopPropagation()}>
          <button className="nms-modal-close" onClick={onClose} aria-label="Close" data-testid="risiko-close">
            <X size={18} />
          </button>
          <div className="anal-loading">Memuat indeks risiko…</div>
        </div>
      </div>
    ) : null;
  }

  const col = statusColor(selected.status);
  const score = selected.score || 0;
  const ringPct = Math.min(100, score) / 100;
  const dash = ringPct * 150.8;

  return (
    <div className="nms-modal-backdrop full-modal" data-testid="risiko-modal" onClick={onClose}>
      <div className="nms-modal risiko-modal" onClick={(e) => e.stopPropagation()}>
        <button className="nms-modal-close" onClick={onClose} aria-label="Close" data-testid="risiko-close">
          <X size={18} />
        </button>

        <header className="rwm-head">
          <Gauge size={22} style={{ color: "#06b6d4" }} />
          <h2 className="rwm-title">INDEKS RISIKO WILAYAH</h2>
        </header>

        <div className="rwm-body">
          <aside className="rwm-left">
            <div className="rwm-left-head">
              <Trophy size={14} style={{ color: "#facc15" }} />
              <span>PERINGKAT WILAYAH</span>
            </div>
            <ul className="rwm-prov-list">
              {sortedProv.map((p, i) => {
                const active = p.code === selected.code;
                return (
                  <li key={p.code}>
                    <button
                      type="button"
                      className={`rwm-prov ${active ? "active" : ""}`}
                      onClick={() => setSelected(p)}
                      data-testid={`rwm-prov-${p.code}`}
                    >
                      <span className="rwm-rank">#{i + 1}</span>
                      <div className="rwm-prov-info">
                        <div className="rwm-pname">{titleCase(p.name)}</div>
                        <span
                          className="rwm-pstatus"
                          style={{ color: statusColor(p.status), borderColor: statusColor(p.status) }}
                        >
                          {p.status?.toUpperCase()}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          <section className="rwm-center">
            <div className="rwm-condition">
              <div className="rwm-gauge">
                <svg viewBox="0 0 60 60" width="120" height="120">
                  <circle cx="30" cy="30" r="24" stroke="rgba(16,185,129,0.15)" strokeWidth="4" fill="none" />
                  <circle
                    cx="30" cy="30" r="24"
                    stroke={col}
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${dash} 150.8`}
                    transform="rotate(-90 30 30)"
                  />
                  <text x="30" y="32" textAnchor="middle" fontSize="11" fontWeight="700" fill="#e2e8f0"
                    fontFamily="IBM Plex Mono">{formatNumber(score, 1)}</text>
                  <text x="30" y="42" textAnchor="middle" fontSize="4" fill="#94a3b8"
                    fontFamily="IBM Plex Mono" letterSpacing="0.5">RISK INDEX</text>
                </svg>
              </div>
              <div className="rwm-cond-body">
                <div className="rwm-cond-head">
                  <span className="rwm-cond-lbl">KONDISI TERKINI</span>
                  <span className="rwm-cond-badge" style={{ color: col, borderColor: col }}>
                    ● {selected.status?.toUpperCase()}
                  </span>
                </div>
                <p className="rwm-cond-desc">
                  Berdasarkan metrik komposit, <strong>{titleCase(selected.name)}</strong> menunjukkan tingkat risiko{" "}
                  <em style={{ color: col }}>{selected.status?.toLowerCase()}</em> dengan deviasi terkendali.
                </p>
              </div>
            </div>

            <div className="rwm-metrics-card">
              <button
                type="button"
                className="rwm-metrics-head"
                onClick={() => setShowMetrics((s) => !s)}
                data-testid="rwm-toggle-metrics"
              >
                <span><LineIcon size={14} style={{ color: "#06b6d4" }} /> RINCIAN METRIK</span>
                <span className="rwm-toggle">TAMPILKAN <ChevronDown size={14} className={showMetrics ? "rot" : ""} /></span>
              </button>
              {showMetrics && (
                <div className="rwm-metrics-body">
                  <div className="rwm-metric-row"><span>Skor Komposit</span><strong style={{ color: col }}>{formatNumber(score, 2)}</strong></div>
                  <div className="rwm-metric-row"><span>Status</span><strong style={{ color: col }}>{selected.status}</strong></div>
                  <div className="rwm-metric-row"><span>Region Code</span><strong>{selected.code}</strong></div>
                  <div className="rwm-metric-row"><span>Indeks Risiko Pangan</span><strong>{formatNumber(Math.max(0, score * 0.42), 1)}</strong></div>
                  <div className="rwm-metric-row"><span>Indeks Risiko Sosial</span><strong>{formatNumber(Math.max(0, score * 0.38), 1)}</strong></div>
                  <div className="rwm-metric-row"><span>Indeks Risiko Ekonomi</span><strong>{formatNumber(Math.max(0, score * 0.31), 1)}</strong></div>
                  <div className="rwm-metric-row"><span>Indeks Risiko Kamtibmas</span><strong>{formatNumber(Math.max(0, score * 0.55), 1)}</strong></div>
                  <div className="rwm-metric-row"><span>Sumber Data</span><strong>BPS · Kemenko · Kemendagri</strong></div>
                  <div className="rwm-metric-row"><span>Update Terakhir</span><strong>{new Date().toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })}</strong></div>
                </div>
              )}
            </div>

            <div className="rwm-history-card">
              <div className="rwm-hist-head">
                <span><LineIcon size={14} style={{ color: "#06b6d4" }} /> POLA HISTORIS</span>
                <div className="rwm-hist-tabs">
                  {Object.keys(RANGE_MAP).map((r) => (
                    <button
                      key={r}
                      type="button"
                      className={`rwm-tab ${range === r ? "active" : ""}`}
                      onClick={() => setRange(r)}
                      data-testid={`rwm-tab-${r.replace(/\s+/g, "_")}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div className="rwm-chart">
                {history.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={history}>
                      <defs>
                        <linearGradient id="rwm-fill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.45} />
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" stroke="#475569" tick={{ fontSize: 9, fill: "#94a3b8" }} />
                      <YAxis stroke="#475569" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                      <RTooltip contentStyle={{ background: "#0a1628", border: "1px solid #10b981", borderRadius: 8, fontSize: 11 }} />
                      <Area type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} fill="url(#rwm-fill)" />
                      {history.length > 0 && (
                        <ReferenceDot
                          x={history[history.length - 1].date}
                          y={history[history.length - 1].value}
                          r={4}
                          fill="#10b981"
                          stroke="#fff"
                        />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="anal-loading">Memuat pola historis…</div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default RisikoWilayahModal;
